/**
 * iter251a — Agora RTC client wrapper.
 *
 * Replaces the LiveKit media layer. Exposes a simple imperative API that
 * `CallContext.js` can drive from its existing state machine.
 *
 * Public API:
 *   - createAgoraSession({ appId, channel, token, uid, publishVideo, callbacks })
 *     → returns { client, localTracks, leave(), setMicEnabled(b), setCameraEnabled(b) }
 *
 * Callbacks:
 *   onRemoteUserPublished(user, mediaType)   — remote subscribed & playable
 *   onRemoteUserUnpublished(user, mediaType)
 *   onRemoteUserLeft(user)
 *   onConnectionStateChange(state, reason)    — agora connection lifecycle
 *   onError(err)
 *
 * NOTE: We intentionally do NOT use a global singleton client. Each call
 * creates its own client so cleanup after a call is bulletproof.
 */
import AgoraRTC from 'agora-rtc-sdk-ng';

// Silence Agora's verbose default logger except on errors.
try { AgoraRTC.setLogLevel(2); } catch { /* no-op */ }

export async function createAgoraSession({
  appId,
  channel,
  token,
  uid,
  publishVideo = false,
  callbacks = {},
  // Group calls: 'live' channel shared with the mobile app; listeners join as audience (no mic, no camera).
  live = false,
  canPublish = true,
}) {
  if (!appId || !channel || !token || !uid) {
    throw new Error('createAgoraSession: missing appId / channel / token / uid');
  }

  const client = AgoraRTC.createClient({ mode: live ? 'live' : 'rtc', codec: 'vp8' });
  if (live) await client.setClientRole(canPublish ? 'host' : 'audience', canPublish ? undefined : { level: 1 });

  // ── Remote stream events ────────────────────────────────────────────
  client.on('user-published', async (user, mediaType) => {
    try {
      await client.subscribe(user, mediaType);
      if (mediaType === 'audio') {
        try { user.audioTrack?.play(); } catch (e) { /* autoplay may be blocked */ }
      }
      callbacks.onRemoteUserPublished?.(user, mediaType);
    } catch (err) {
      callbacks.onError?.(err);
    }
  });
  client.on('user-unpublished', (user, mediaType) => {
    callbacks.onRemoteUserUnpublished?.(user, mediaType);
  });
  client.on('user-left', (user) => {
    callbacks.onRemoteUserLeft?.(user);
  });
  client.on('connection-state-change', (curState, prevState, reason) => {
    callbacks.onConnectionStateChange?.(curState, reason, prevState);
  });
  // Group speakers get a short publish right: the caller fetches a fresh token (and the current seat) from the server.
  client.on('token-privilege-will-expire', () => callbacks.onTokenWillExpire?.());
  client.on('token-privilege-did-expire', () => callbacks.onTokenWillExpire?.());

  // ── Join channel ────────────────────────────────────────────────────
  await client.join(appId, channel, token, uid);

  // ── Create + publish local tracks ───────────────────────────────────
  let micTrack = null;
  let camTrack = null;
  if (canPublish) {
    try {
      micTrack = await AgoraRTC.createMicrophoneAudioTrack();
    } catch (e) {
      // Critical: no mic = no call. Let the caller decide how to surface this.
      await client.leave().catch(() => {});
      throw e;
    }
  }
  if (publishVideo && canPublish) {
    try {
      camTrack = await AgoraRTC.createCameraVideoTrack({
        encoderConfig: '480p_1',
      });
    } catch (e) {
      // Non-fatal: continue as audio-only and let the caller toast it.
      callbacks.onError?.(e);
    }
  }

  const publishList = [micTrack, camTrack].filter(Boolean);
  if (publishList.length) {
    await client.publish(publishList);
  }

  // ── Imperative controls ─────────────────────────────────────────────
  async function setMicEnabled(enabled) {
    if (!micTrack) return;
    try { await micTrack.setEnabled(!!enabled); } catch (e) { callbacks.onError?.(e); }
  }
  async function setCameraEnabled(enabled) {
    if (enabled && !camTrack) {
      // Lazy creation: user toggled camera on after starting audio-only.
      try {
        camTrack = await AgoraRTC.createCameraVideoTrack({ encoderConfig: '480p_1' });
        await client.publish([camTrack]);
      } catch (e) { callbacks.onError?.(e); }
      return;
    }
    if (!camTrack) return;
    try { await camTrack.setEnabled(!!enabled); } catch (e) { callbacks.onError?.(e); }
  }
  // Server granted / withdrew a speaking seat: renew the token (new role), then publish or go back to audience.
  let publishing = canPublish;
  async function renew(token, publish) {
    try {
      if (live && publish !== publishing) await setRole(publish, token);
      else await client.renewToken(token);
    } catch (e) { callbacks.onError?.(e); }
  }
  async function setRole(publish, token) {
    publishing = publish;
    try {
      if (token) await client.renewToken(token);
      if (publish) {
        await client.setClientRole('host');
        if (!micTrack) micTrack = await AgoraRTC.createMicrophoneAudioTrack();
        await client.publish([micTrack]);
      } else {
        const tracks = [micTrack, camTrack].filter(Boolean);
        if (tracks.length) await client.unpublish(tracks).catch(() => {});
        tracks.forEach((tr) => { try { tr.stop(); tr.close(); } catch { /* ignore */ } });
        micTrack = null; camTrack = null;
        await client.setClientRole('audience', { level: 1 });
      }
    } catch (e) { callbacks.onError?.(e); }
  }
  async function leave() {
    try {
      const toUnpublish = [micTrack, camTrack].filter(Boolean);
      if (toUnpublish.length) await client.unpublish(toUnpublish);
    } catch { /* ignore */ }
    try { micTrack?.stop(); micTrack?.close(); } catch { /* ignore */ }
    try { camTrack?.stop(); camTrack?.close(); } catch { /* ignore */ }
    try { await client.leave(); } catch { /* ignore */ }
  }

  return {
    client,
    get micTrack() { return micTrack; },
    get camTrack() { return camTrack; },
    setMicEnabled,
    setCameraEnabled,
    setRole,
    renew,
    get publishing() { return publishing; },
    leave,
  };
}

/**
 * Play an Agora remote video track into a DOM element by id.
 * Used by VideoCallGrid which mounts <div ref> per participant.
 */
export function playRemoteVideo(videoTrack, container) {
  if (!videoTrack || !container) return;
  try { videoTrack.play(container, { fit: 'cover' }); } catch (e) { /* no-op */ }
}
